import { useState, useEffect } from "react";
import { Bell, CheckCircle, Clock, Coffee, Flag, LayoutDashboard, Loader, Pill, PlaneTakeoff, Star, Users, X } from "lucide-react";
import { supabase } from "./supabase";
import { KO, POLICY, fmtDate, dispType, sendPushToAll, getHolidays, getAllocatedVac, getPolicy } from "./helpers";

export function Dashboard({t,lang,isAdmin,empName,refreshKey,goPage}){
  const [employees,setEmp]=useState([]);
  const [leaves,setLeaves]=useState([]);
  const [evals,setEvals]=useState([]);
  const [modal,setModal]=useState(null);
  const [safetyRecs,setSafety]=useState([]);
  const [policy,setPolicy]=useState(null);
  useEffect(()=>{
    getPolicy().then(setPolicy);
  },[]);
  const wh = policy ? {start:policy.work_start_en,end:policy.work_end_en,days:policy.work_days_en} : POLICY.workHours;
  const whKo = policy ? {start:policy.work_start_kr,end:policy.work_end_kr,days:policy.work_days_kr} : POLICY.workHoursKo;
  const policyBreaks = policy?.breaks || POLICY.breaks;
  const policySickDays = policy?.sick_days ?? POLICY.sickDays;
  useEffect(()=>{
    supabase.from("employees").select("*").then(({data})=>{
      if(data){
        // 대시보드는 재직중(Active) 직원만 표시 (퇴사 처리된 직원은 휴가잔여/생일/평가 현황 등에서 제외)
        const activeOnly=data.filter(e=>e.status!=="Inactive");
        setEmp(isAdmin?activeOnly:activeOnly.filter(e=>e.name===empName));
      }
    });
    supabase.from("leaves").select("*").is("clock_in",null).then(({data})=>{
      if(data) setLeaves(isAdmin?data:data.filter(l=>l.emp_name===empName));
    });
    supabase.from("evals").select("*").then(({data})=>{ if(data) setEvals(isAdmin?data:data.filter(e=>e.emp_name===empName)); });
    supabase.from("safety_training").select("*").then(({data})=>data&&setSafety(data));
  },[refreshKey]);
  const d=t.dash; const lt=t.leave.types;
  const now=new Date(); const todayStr=fmtDate(now); const thisMonth=todayStr.slice(0,7);
  const thisYear=todayStr.slice(0,4);
  const hh=now.getHours();
  const timeStr=now.toLocaleTimeString(lang==="kr"?"ko-KR":lang==="es"?"es-US":"en-US",{hour:"2-digit",minute:"2-digit"});
  const isWork=hh>=8&&hh<17&&now.getDay()>=1&&now.getDay()<=5;

  // 오늘 휴가자
  const todayLeaveList=leaves.filter(l=>l.status==="approved"&&l.date<=todayStr&&(l.end_date||l.date)>=todayStr);
  // 이번달 근태
  const monthLeaveList=leaves.filter(l=>l.type!=="특이사항"&&l.type!=="Note"&&l.type!=="Special Note"&&l.status==="approved"&&l.date<=thisMonth+"-31"&&(l.end_date||l.date)>=thisMonth+"-01");
  // 이번주 휴가
  const weekStart=new Date(now); weekStart.setDate(now.getDate()-now.getDay());
  const weekEnd=new Date(weekStart); weekEnd.setDate(weekStart.getDate()+6);
  const weekStartStr=fmtDate(weekStart); const weekEndStr=fmtDate(weekEnd);
  const weekLeaveList=leaves.filter(l=>{
    if(!l.date) return false;
    if(!(l.type==="연차"||l.type==="병가"||l.type==="Annual Leave"||l.type==="Sick Leave")) return false;
    if(l.status!=="approved") return false;
    return l.date<=weekEndStr&&(l.end_date||l.date)>=weekStartStr;
  });
  // 올해 성과평가
  const yearEvalList=evals.filter(e=>e.period?.startsWith(thisYear));
  const evaledNames=new Set(yearEvalList.map(e=>e.emp_name));
  const notEvaledEmps=employees.filter(e=>!evaledNames.has(e.name));
  const yearLeaves=leaves.filter(l=>l.date?.startsWith(thisYear));
  const recent=[...leaves].sort((a,b)=>b.date?.localeCompare(a.date)).slice(0,5);

  return(
    <>
      <div className="ph"><div className="pt">{d.title||t.nav.dash}</div><div className="ps">{t.today(now)}</div></div>
      {/* work hours bar */}
      <div className="card" style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12,marginBottom:20}}>
        <div style={{display:"flex",gap:16,flexWrap:"wrap",flex:1}}>
          <div><div className="sl"><Clock size={14} style={{marginRight:4}}/>{t.policy.workTitle}</div><div style={{fontFamily:"'DM Mono',monospace",fontWeight:700,fontSize:13}}>{policy===null?(lang==="kr"?"불러오는 중...":lang==="es"?"Cargando...":"Loading..."):(lang==="kr"?`${whKo.start}–${whKo.end} (${whKo.days})`:`${wh.start}–${wh.end} (${wh.days})`)}</div></div>
          {policy!==null && policyBreaks.map((b,i)=>(
            <div key={i} className="hm"><div className="sl"><Coffee size={14} style={{marginRight:4}}/>{lang==="kr"?b.labelKo:b.label}</div><div style={{fontFamily:"'DM Mono',monospace",fontWeight:600,fontSize:12}}>{b.time} <span style={{color:"var(--tx3)",fontSize:11}}>({lang==="kr"?b.durKo:b.dur})</span></div></div>
          ))}
          <div className="hm-mobile" style={{display:"none",flexDirection:"column",gap:3,width:"100%"}}>
            <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8}}>
              <div style={{display:"flex",flexDirection:"column",gap:3}}>
                {policy!==null && policyBreaks.map((b,i)=>(
                  <div key={i} style={{fontSize:11,color:"var(--tx2)"}}><Coffee size={11} style={{marginRight:3,verticalAlign:"middle"}}/>{lang==="kr"?b.labelKo:b.label}: {b.time}</div>
                ))}
              </div>
              <div style={{background:isWork?"rgba(5,150,105,.1)":"rgba(100,116,139,.1)",border:`1px solid ${isWork?"rgba(5,150,105,.3)":"var(--bd)"}`,borderRadius:10,padding:"8px 16px",textAlign:"center",flexShrink:0}}>
                <div style={{fontSize:11,color:"var(--tx3)",marginBottom:2}}>{isWork?d.onDuty:d.offHours}</div>
                <div style={{fontFamily:"'DM Mono',monospace",fontWeight:700,fontSize:17,color:isWork?"var(--gr)":"var(--tx3)"}}>{timeStr}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="hm" style={{flexShrink:0}}>
          <div style={{background:isWork?"rgba(5,150,105,.1)":"rgba(100,116,139,.1)",border:`1px solid ${isWork?"rgba(5,150,105,.3)":"var(--bd)"}`,borderRadius:10,padding:"8px 16px",textAlign:"center"}}>
            <div style={{fontSize:11,color:"var(--tx3)",marginBottom:2}}>{isWork?d.onDuty:d.offHours}</div>
            <div style={{fontFamily:"'DM Mono',monospace",fontWeight:700,fontSize:17,color:isWork?"var(--gr)":"var(--tx3)"}}>{timeStr}</div>
          </div>
        </div>
      </div>
      {/* stat cards - 클릭 가능 */}
      <div className="sg" style={{gridTemplateColumns:"repeat(5,1fr)"}}>
        <div className="sc bl" style={{cursor:"pointer"}} onClick={()=>setModal("today")}>
          <div className="sl">{d.todayAbsent}</div>
          <div className="sv">{todayLeaveList.length}</div>
          <div style={{fontSize:11,color:"var(--ac)",marginTop:6}}>{lang==="kr"?"▶ 상세보기":lang==="es"?"▶ Ver detalles":"▶ View"}</div>
        </div>
        <div className="sc pu" style={{cursor:"pointer"}} onClick={()=>setModal("month")}>
          <div className="sl">{d.monthLeave}</div>
          <div className="sv">{monthLeaveList.length}</div>
          <div style={{fontSize:11,color:"var(--ac2)",marginTop:6}}>{lang==="kr"?"▶ 상세보기":lang==="es"?"▶ Ver detalles":"▶ View"}</div>
        </div>
        <div className="sc gr" style={{cursor:"pointer"}} onClick={()=>setModal("week")}>
          <div className="sl">{d.thisWeekLeave}</div>
          <div className="sv">{weekLeaveList.length}</div>
          <div style={{fontSize:11,color:"var(--gr)",marginTop:6}}>{lang==="kr"?"▶ 상세보기":lang==="es"?"▶ Ver detalles":"▶ View"}</div>
        </div>
        <div className="sc or" style={{cursor:"pointer"}} onClick={()=>setModal("yeareval")}>
          <div className="sl">{d.yearEval}</div>
          <div className="sv">{yearEvalList.length}<span style={{fontSize:14,color:"var(--tx3)",fontWeight:400}}>/{employees.length}</span></div>
          <div style={{fontSize:11,color:"var(--or)",marginTop:6}}>{lang==="kr"?"▶ 상세보기":lang==="es"?"▶ Ver detalles":"▶ View"}</div>
        </div>
        <div className="sc" style={{cursor:"pointer","--ac":"#8b5cf6"}} onClick={()=>setModal("safety")}>
          <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:"#8b5cf6",borderRadius:"12px 12px 0 0"}}/>
          <div className="sl">{d.safetyQ}</div>
          {(()=>{
            const curQ=`Q${Math.ceil((new Date().getMonth()+1)/3)}`;
            const curY=new Date().getFullYear().toString();
            const done=safetyRecs.filter(r=>r.quarter===curQ&&parseInt(r.year)===parseInt(curY));
            const signed=done.filter(r=>{const atts=r.attendees||r.selected_emps||[];const sigs=r.signatures||{};return atts.length>0&&atts.every(n=>sigs[n]);});
            return(<>
              <div className="sv" style={{color:"#8b5cf6"}}>{signed.length}<span style={{fontSize:14,color:"var(--tx3)",fontWeight:400}}>/{done.length||"?"}</span></div>
              <div style={{fontSize:10,color:"var(--tx3)",marginTop:2}}>{curQ} {curY}</div>
              <div style={{fontSize:11,color:"#8b5cf6",marginTop:4}}>{lang==="kr"?"▶ 상세보기":lang==="es"?"▶ Ver detalles":"▶ View"}</div>
            </>);
          })()}
        </div>
      </div>

      {/* 모달 */}
      {modal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setModal(null)}>
          <div style={{background:"var(--sf)",borderRadius:16,padding:24,width:"100%",maxWidth:480,maxHeight:"80vh",overflowY:"auto",boxShadow:"0 8px 40px rgba(0,0,0,.3)"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <div style={{fontSize:16,fontWeight:700}}>
                {modal==="today"&&(lang==="kr"?"오늘 결근/휴가":lang==="es"?"Ausentes hoy":"Today Absent")}
                {modal==="month"&&(lang==="kr"?"이번 달 근태":lang==="es"?"Ausencias del mes":"This Month Leave")}
                {modal==="week"&&(lang==="kr"?"이번 주 휴가":lang==="es"?"Ausentes esta semana":"This Week Off")}
                {modal==="safety"&&(lang==="kr"?"분기 안전교육":lang==="es"?"Capacitación de seguridad":"Safety Training")}
            {modal==="yeareval"&&(lang==="kr"?"올해 성과평가 현황":lang==="es"?"Evaluaciones del año":"This Year Evals")}
              </div>
              <button onClick={()=>setModal(null)} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:"var(--tx3)"}}><X size={18}/></button>
            </div>

            {modal==="today"&&(
              todayLeaveList.length===0
                ?<div className="empty">{lang==="kr"?"오늘 휴가자 없음":lang==="es"?"Nadie ausente hoy":"No one off today"}</div>
                :todayLeaveList.map((l,i)=>(
                  <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid var(--bd)"}}>
                    <span className="etag"><span className="edot"/>{l.emp_name}</span>
                    <div style={{textAlign:"right"}}>
                      <span className={`bg b${lt.indexOf(l.type)}`}>{dispType(l.type,lang)}</span>
                      {l.reason&&<div style={{fontSize:11,color:"var(--tx3)",marginTop:3}}>{l.reason}</div>}
                    </div>
                  </div>
                ))
            )}

            {modal==="month"&&(
              monthLeaveList.length===0
                ?<div className="empty">{lang==="kr"?"이번 달 근태 없음":lang==="es"?"Sin registros este mes":"No records this month"}</div>
                :monthLeaveList.sort((a,b)=>b.date?.localeCompare(a.date)).map((l,i)=>(
                  <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid var(--bd)"}}>
                    <div>
                      <span className="etag"><span className="edot"/>{l.emp_name}</span>
                      <div style={{fontSize:11,color:"var(--tx3)",marginTop:3}}>{l.date}{l.end_date&&l.end_date!==l.date?` ~ ${l.end_date}`:""} · {l.days}{t.dayUnit}</div>
                    </div>
                    <span className={`bg b${lt.indexOf(l.type)}`}>{dispType(l.type,lang)}</span>
                  </div>
                ))
            )}

            {modal==="week"&&(
              weekLeaveList.length===0
                ?<div className="empty">{lang==="kr"?"이번 주 휴가 없음":lang==="es"?"Nadie ausente esta semana":"No one off this week"}</div>
                :weekLeaveList.sort((a,b)=>a.date?.localeCompare(b.date)).map((l,i)=>(
                  <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid var(--bd)"}}>
                    <div>
                      <span className="etag"><span className="edot"/>{l.emp_name}</span>
                      <div style={{fontSize:11,color:"var(--tx3)",marginTop:3}}>{l.date}{l.end_date&&l.end_date!==l.date?` ~ ${l.end_date}`:""} · {l.days}{t.dayUnit}</div>
                    </div>
                    <span className={`bg b${lt.indexOf(l.type)}`}>{dispType(l.type,lang)}</span>
                  </div>
                ))
            )}

            {modal==="safety"&&(
              <div>
                {(()=>{
                  const curQ=`Q${Math.ceil((new Date().getMonth()+1)/3)}`;
                  const curY=new Date().getFullYear().toString();
                  const recs=safetyRecs.filter(r=>r.quarter===curQ&&parseInt(r.year)===parseInt(curY));
                  return recs.length===0
                    ?<div className="empty">{lang==="kr"?`${curQ} ${curY} 기록 없음`:lang==="es"?`Sin registros de ${curQ} ${curY}`:`No ${curQ} ${curY} records`}</div>
                    :recs.map((r,i)=>(
                      <div key={i} style={{padding:"10px 0",borderBottom:"1px solid var(--bd)"}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                          <div>
                            <span style={{fontWeight:700}}>{r.quarter} {r.year}</span>
                            {r.date&&<span style={{fontSize:12,color:"var(--tx3)",marginLeft:8}}>{r.date}</span>}
                            {r.manager&&<span style={{fontSize:12,color:"var(--tx3)",marginLeft:8}}><CheckCircle size={12} style={{marginRight:4}}/>{r.manager}</span>}
                          </div>
                          {(()=>{const atts=r.attendees||r.selected_emps||[];const sigs=r.signatures||{};const allSigned=atts.length>0&&atts.every(n=>sigs[n]);const cnt=atts.filter(n=>sigs[n]).length;return(<span style={{fontSize:11,fontWeight:700,color:allSigned?"var(--gr)":"var(--ye)"}}>
                            {allSigned?(lang==="kr"?"서명완료":lang==="es"?"Firmado":"Signed"):`${cnt}/${atts.length} ${lang==="kr"?"서명":lang==="es"?"Firmado":"Signed"}`}
                          </span>);})()}
                        </div>
                        {r.selected_emps?.length>0&&<div style={{fontSize:12,color:"var(--tx2)",marginTop:4}}><Users size={12}/> {r.selected_emps.join(", ")}</div>}
                      </div>
                    ));
                })()}
              </div>
            )}
            {modal==="yeareval"&&(
              <div>
                <div style={{marginBottom:12,fontSize:13,color:"var(--tx2)"}}>
                  {lang==="kr"?`완료 ${evaledNames.size}명 / 미실시 ${notEvaledEmps.length}명`:lang==="es"?`Completados: ${evaledNames.size} / Pendientes: ${notEvaledEmps.length}`:`Completed: ${evaledNames.size} / Not Yet: ${notEvaledEmps.length}`}
                </div>
                {employees.map(emp=>(
                  <div key={emp.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid var(--bd)"}}>
                    <span className="etag"><span className="edot"/>{emp.name}</span>
                    {evaledNames.has(emp.name)
                      ?<span style={{fontSize:12,fontWeight:700,color:"var(--gr)"}}><CheckCircle size={12} style={{marginRight:4}}/>{lang==="kr"?"완료":lang==="es"?"Listo":"Done"}</span>
                      :<span style={{fontSize:12,fontWeight:700,color:"var(--re)"}}><Loader size={12} style={{marginRight:4}}/>{lang==="kr"?"미실시":lang==="es"?"Pendiente":"Not Yet"}</span>
                    }
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* announcements */}
      <AnnouncementCard t={t} lang={lang} isAdmin={isAdmin} goPage={goPage}/>
      <DashBottomCards employees={employees} yearLeaves={yearLeaves} recent={recent} lt={lt} t={t} lang={lang} policySickDays={policySickDays} policyVacTiers={policy?.vacation_tiers}/>
    </>
  );
}

function isCurrentMonth(dateStr){
  const d=new Date(dateStr);
  const now=new Date();
  return d.getFullYear()===now.getFullYear() && d.getMonth()===now.getMonth();
}

function AnnouncementCard({t, lang, isAdmin, goPage}){
  const KO=lang==="kr"; const SP=lang==="es";
  const [notices,setNotices]=useState([]);
  const [editing,setEditing]=useState(null);
  const [nForm,setNForm]=useState({title:"",content:""});
  const [banner,setBanner]=useState(null);
  const [seenId,setSeenId]=useState(()=>localStorage.getItem("lastSeenNotice")||"");
  const currentNotices=notices.filter(n=>isCurrentMonth(n.created_at));

  useEffect(()=>{
    supabase.from("policy_notes").select("*").order("created_at",{ascending:false}).then(({data})=>{
      if(data){
        setNotices(data);
        if(data.length>0&&data[0].id!==seenId) setBanner(data[0]);
      }
    });
  },[]);

  function dismissBanner(){
    if(banner){localStorage.setItem("lastSeenNotice",banner.id);setSeenId(banner.id);}
    setBanner(null);
  }

  async function saveNotice(){
    if(!nForm.content.trim()) return;
    if(editing==="new"){ await supabase.from("policy_notes").insert([{title:"",content:nForm.content}]); await sendPushToAll(supabase, "새 공지사항", "새 공지사항이 등록되었습니다. 확인해주세요.", "New Announcement", "A new announcement has been posted. Please check it out.", "/dashboard", {title:"Nuevo anuncio", body:"Se publicó un nuevo anuncio. Por favor, revísalo."}); }
    else await supabase.from("policy_notes").update({title:"",content:nForm.content}).eq("id",editing.id);
    setEditing(null); setNForm({title:"",content:""});
    supabase.from("policy_notes").select("*").order("created_at",{ascending:false}).then(({data})=>data&&setNotices(data));
  }

  async function delNotice(id){
    if(window.confirm(KO?"삭제하시겠습니까?":SP?"¿Eliminar?":"Delete?"))
      await supabase.from("policy_notes").delete().eq("id",id).then(()=>
        supabase.from("policy_notes").select("*").order("created_at",{ascending:false}).then(({data})=>data&&setNotices(data)));
  }

  return(
    <>
      <div className="card" style={{marginBottom:20}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <div className="ct" style={{margin:0}}><Bell size={15}/> {KO?"공지사항":SP?"Anuncios":"Announcements"}</div>
          {isAdmin&&<button className="btn bs" style={{background:"var(--ac)",color:"#fff"}} onClick={()=>{setEditing("new");setNForm({title:"",content:""});}}>+ {KO?"추가":SP?"Agregar":"Add"}</button>}
        </div>
        {currentNotices.length===0&&<div style={{fontSize:12,color:"var(--tx3)"}}>{KO?"이번 달 등록된 공지사항이 없습니다.":SP?"No hay anuncios este mes.":"No announcements this month."}</div>}
        {currentNotices.slice(0,2).map(n=>(
          <div key={n.id} style={{background:"var(--sf2)",borderRadius:8,padding:"12px 14px",marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center",gap:10}}>
            <div style={{fontSize:12,color:"var(--tx2)",whiteSpace:"pre-wrap",flex:1,wordBreak:"break-word",overflowWrap:"break-word",minWidth:0}}>{n.content}</div>
            {isAdmin&&<div style={{display:"flex",gap:4,flexShrink:0}}>
              <button className="btn bs" style={{fontSize:11,background:"var(--ac)",color:"#fff"}} onClick={()=>{setEditing(n);setNForm({title:"",content:n.content});}}>{KO?"수정":SP?"Editar":"Edit"}</button>
              <button className="btn bs bd2" style={{fontSize:11}} onClick={()=>delNotice(n.id)}>{KO?"삭제":SP?"Eliminar":"Delete"}</button>
            </div>}
          </div>
        ))}
        <div style={{textAlign:"center",marginTop:6}}>
          <button onClick={()=>goPage("announcements")} style={{background:"none",border:"none",color:"var(--ac)",fontSize:12,fontWeight:600,cursor:"pointer"}}>
            {KO?`전체보기 (지난 공지 포함, 총 ${notices.length})`:SP?`Ver todo (incluye anteriores, ${notices.length} en total)`:`View All (incl. past, ${notices.length} total)`} →
          </button>
        </div>
        {editing&&(
          <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,.4)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <div style={{background:"#fff",borderRadius:16,padding:28,width:"100%",maxWidth:480}}>
              <div style={{fontWeight:800,fontSize:17,marginBottom:16}}>{editing==="new"?(KO?"공지 추가":SP?"Agregar anuncio":"Add Notice"):(KO?"공지 수정":SP?"Editar anuncio":"Edit Notice")}</div>
              <textarea value={nForm.content} onChange={e=>setNForm(p=>({...p,content:e.target.value}))} placeholder={KO?"내용":SP?"Contenido":"Content"} rows={5} style={{width:"100%",padding:"8px 12px",borderRadius:8,border:"1px solid #e0e0e0",fontSize:13,boxSizing:"border-box",resize:"vertical"}}/>
              <div style={{display:"flex",gap:8,marginTop:14,justifyContent:"flex-end"}}>
                <button onClick={()=>setEditing(null)} style={{padding:"8px 18px",borderRadius:8,border:"1px solid #e0e0e0",background:"#f5f5f5",cursor:"pointer",fontWeight:600}}>{KO?"취소":SP?"Cancelar":"Cancel"}</button>
                <button onClick={saveNotice} style={{padding:"8px 20px",borderRadius:8,border:"none",background:"#1a1a1a",color:"#fff",cursor:"pointer",fontWeight:700}}>{KO?"저장":SP?"Guardar":"Save"}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export function AnnouncementBoard({t, lang, isAdmin}){
  const KO=lang==="kr"; const SP=lang==="es";
  const [notices,setNotices]=useState([]);
  const [editing,setEditing]=useState(null);
  const [nForm,setNForm]=useState({content:""});
  const [search,setSearch]=useState("");
  const [showPast,setShowPast]=useState(false);

  useEffect(()=>{
    reload();
  },[]);

  function reload(){
    supabase.from("policy_notes").select("*").order("created_at",{ascending:false}).then(({data})=>data&&setNotices(data));
  }

  async function saveNotice(){
    if(!nForm.content.trim()) return;
    if(editing==="new"){
      await supabase.from("policy_notes").insert([{title:"",content:nForm.content}]);
      await sendPushToAll(supabase, "새 공지사항", "새 공지사항이 등록되었습니다. 확인해주세요.", "New Announcement", "A new announcement has been posted. Please check it out.", "/dashboard", {title:"Nuevo anuncio", body:"Se publicó un nuevo anuncio. Por favor, revísalo."});
    } else {
      await supabase.from("policy_notes").update({title:"",content:nForm.content}).eq("id",editing.id);
    }
    setEditing(null); setNForm({content:""});
    reload();
  }

  async function delNotice(id){
    if(window.confirm(KO?"삭제하시겠습니까?":SP?"¿Eliminar?":"Delete?")){
      await supabase.from("policy_notes").delete().eq("id",id);
      reload();
    }
  }

  const filteredNotices = search.trim()
    ? notices.filter(n=>n.content?.toLowerCase().includes(search.trim().toLowerCase()))
    : notices;
  const currentNotices = filteredNotices.filter(n=>isCurrentMonth(n.created_at));
  const pastNotices = filteredNotices.filter(n=>!isCurrentMonth(n.created_at));

  function renderNotice(n){
    return(
      <div key={n.id} style={{background:"var(--sf2)",borderRadius:8,padding:"12px 14px",marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:13,color:"var(--tx2)",whiteSpace:"pre-wrap",wordBreak:"break-word",overflowWrap:"break-word"}}>{n.content}</div>
          <div style={{fontSize:11,color:"var(--tx3)",marginTop:6}}>{new Date(n.created_at).toLocaleString()}</div>
        </div>
        {isAdmin&&<div style={{display:"flex",gap:4,flexShrink:0}}>
          <button className="btn bs" style={{fontSize:11,background:"var(--ac)",color:"#fff"}} onClick={()=>{setEditing(n);setNForm({content:n.content});}}>{KO?"수정":SP?"Editar":"Edit"}</button>
          <button className="btn bs bd2" style={{fontSize:11}} onClick={()=>delNotice(n.id)}>{KO?"삭제":SP?"Eliminar":"Delete"}</button>
        </div>}
      </div>
    );
  }

  return(
    <>
      <div className="ph"><div className="pt"><Bell size={18} style={{marginRight:6,verticalAlign:"middle"}}/>{KO?"공지사항":SP?"Anuncios":"Announcements"}</div></div>
      <div className="card">
        <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap"}}>
          {isAdmin&&(
            <button className="btn bs" style={{background:"var(--ac)",color:"#fff"}} onClick={()=>{setEditing("new");setNForm({content:""});}}>+ {KO?"추가":SP?"Agregar":"Add"}</button>
          )}
          <input
            value={search}
            onChange={e=>setSearch(e.target.value)}
            placeholder={KO?"공지사항 검색...":SP?"Buscar anuncios...":"Search announcements..."}
            style={{flex:1,minWidth:160,padding:"8px 12px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx)",fontSize:13,outline:"none",boxSizing:"border-box"}}
          />
        </div>

        <div className="ct" style={{marginBottom:8}}>{KO?"이번 달":SP?"Este mes":"This Month"}</div>
        {currentNotices.length===0&&(
          <div style={{fontSize:12,color:"var(--tx3)",marginBottom:8}}>
            {search.trim()?(KO?"검색 결과가 없습니다.":SP?"No se encontraron resultados.":"No results found."):(KO?"이번 달 등록된 공지사항이 없습니다.":SP?"No hay anuncios este mes.":"No announcements this month.")}
          </div>
        )}
        {currentNotices.map(renderNotice)}

        {pastNotices.length>0&&(
          <>
            <div className="ct" style={{margin:"16px 0 8px",cursor:"pointer",userSelect:"none",color:"var(--tx3)"}} onClick={()=>setShowPast(s=>!s)}>
              {KO?"지난 공지 (기록)":SP?"Anuncios anteriores (archivo)":"Past Announcements (Archive)"} ({pastNotices.length}) {showPast?"▲":"▼"}
            </div>
            {showPast&&pastNotices.map(renderNotice)}
          </>
        )}
      </div>
      {editing&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,.4)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"#fff",borderRadius:16,padding:28,width:"100%",maxWidth:480}}>
            <div style={{fontWeight:800,fontSize:17,marginBottom:16}}>{editing==="new"?(KO?"공지 추가":SP?"Agregar anuncio":"Add Notice"):(KO?"공지 수정":SP?"Editar anuncio":"Edit Notice")}</div>
            <textarea value={nForm.content} onChange={e=>setNForm(p=>({...p,content:e.target.value}))} placeholder={KO?"내용":SP?"Contenido":"Content"} rows={5} style={{width:"100%",padding:"8px 12px",borderRadius:8,border:"1px solid #e0e0e0",fontSize:13,boxSizing:"border-box",resize:"vertical"}}/>
            <div style={{display:"flex",gap:8,marginTop:14,justifyContent:"flex-end"}}>
              <button onClick={()=>setEditing(null)} style={{padding:"8px 18px",borderRadius:8,border:"1px solid #e0e0e0",background:"#f5f5f5",cursor:"pointer",fontWeight:600}}>{KO?"취소":SP?"Cancelar":"Cancel"}</button>
              <button onClick={saveNotice} style={{padding:"8px 20px",borderRadius:8,border:"none",background:"#1a1a1a",color:"#fff",cursor:"pointer",fontWeight:700}}>{KO?"저장":SP?"Guardar":"Save"}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function BirthdayHolidayCard({employees,lang}){
  const KO=lang==="kr"; const SP=lang==="es";
  const now=new Date();
  const thisMonth=now.getMonth()+1;
  const thisYear=now.getFullYear();

  // 미국 공휴일 (고정)
  const usHolidays=[
    {month:1,day:1,name:"New Year's Day"},
    {month:1,day:20,name:"MLK Day"},
    {month:2,day:17,name:"Presidents' Day"},
    {month:5,day:26,name:"Memorial Day"},
    {month:6,day:19,name:"Juneteenth"},
    {month:7,day:4,name:"Independence Day"},
    {month:9,day:1,name:"Labor Day"},
    {month:10,day:13,name:"Columbus Day"},
    {month:11,day:11,name:"Veterans Day"},
    {month:11,day:27,name:"Thanksgiving"},
    {month:12,day:25,name:"Christmas Day"},
  ];

  const monthHolidays=usHolidays.filter(h=>h.month===thisMonth).sort((a,b)=>a.day-b.day);

  // 이번 달 생일 직원
  const monthBirthdays=employees.filter(emp=>{
    if(!emp.birthday) return false;
    const bd=emp.birthday;
    // MM/DD/YYYY or YYYY-MM-DD 둘 다 지원
    const m=bd.includes("-")?parseInt(bd.slice(5,7)):parseInt(bd.slice(0,2));
    return m===thisMonth;
  }).sort((a,b)=>{
    const getDay=(bd)=>bd.includes("-")?parseInt(bd.slice(8,10)):parseInt(bd.slice(3,5));
    return getDay(a.birthday)-getDay(b.birthday);
  });

  const monthNamesEs=["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];
  const holidayEs={"New Year's Day":"Año Nuevo","MLK Day":"Día de Martin Luther King Jr.","Presidents' Day":"Día de los Presidentes","Memorial Day":"Día de los Caídos","Juneteenth":"Juneteenth","Independence Day":"Día de la Independencia","Labor Day":"Día del Trabajo","Columbus Day":"Día de la Raza (Colón)","Veterans Day":"Día de los Veteranos","Thanksgiving":"Día de Acción de Gracias","Christmas Day":"Navidad"};
  const monthNames=["January","February","March","April","May","June","July","August","September","October","November","December"];

  return(
    <div className="card" style={{marginBottom:20}}>
      <div className="ct" style={{marginBottom:12}}><Star size={15}/> {KO?`${thisMonth}월 생일 & 공휴일`:SP?`Cumpleaños y feriados de ${monthNamesEs[thisMonth-1]}`:`${monthNames[thisMonth-1]} Birthdays & Holidays`}</div>
      {monthBirthdays.length>0&&(
        <>
          <div style={{fontSize:11,fontWeight:700,color:"var(--tx3)",marginBottom:6,textTransform:"uppercase",letterSpacing:.5}}>{KO?"생일":SP?"Cumpleaños":"Birthdays"}</div>
          {monthBirthdays.map((emp,i)=>(
            <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderBottom:"1px solid var(--bd)"}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{width:8,height:8,borderRadius:"50%",background:"var(--ac)",display:"inline-block"}}/>
                <span style={{fontSize:13,fontWeight:600}}>{emp.name}</span>
                {emp.position&&<span style={{fontSize:11,color:"var(--tx3)"}}>{emp.position}</span>}
              </div>
              <span style={{fontSize:12,color:"var(--ac)",fontWeight:700}}>{emp.birthday.includes("-")?parseInt(emp.birthday.slice(8,10)):parseInt(emp.birthday.slice(3,5))}{KO?"일":SP?"":""}</span>
            </div>
          ))}
        </>
      )}
      {monthBirthdays.length===0&&<div style={{fontSize:12,color:"var(--tx3)",marginBottom:8}}>{KO?"이번 달 생일인 직원이 없습니다.":SP?"No hay cumpleaños este mes.":"No birthdays this month."}</div>}
      <div style={{marginTop:12}}>
        <div style={{fontSize:11,fontWeight:700,color:"var(--tx3)",marginBottom:6,textTransform:"uppercase",letterSpacing:.5}}>{KO?"공휴일":SP?"Feriados de EE. UU.":"US Holidays"}</div>
        {monthHolidays.length===0
          ?<div style={{fontSize:12,color:"var(--tx3)"}}>{KO?"이번 달 공휴일 없음":SP?"No hay feriados este mes.":"No holidays this month."}</div>
          :monthHolidays.map((h,i)=>(
            <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderBottom:"1px solid var(--bd)"}}>
              <span style={{fontSize:13}}>{SP?(holidayEs[h.name]||h.name):h.name}</span>
              <span style={{fontSize:12,color:"var(--re)",fontWeight:700}}>{thisMonth}/{h.day}</span>
            </div>
          ))
        }
      </div>
    </div>
  );
}

function RemainCard({employees,yearLeaves,t,lang,policySickDays,policyVacTiers}){
  const lt=t.leave.types; const d=t.dash;
  const [open,setOpen]=useState(true);
  return(
    <div className="card" style={{marginBottom:20}}>
      <div className="ct" style={{cursor:"pointer",userSelect:"none"}} onClick={()=>setOpen(o=>!o)}>
        <LayoutDashboard size={15}/> {d.remainTitle}
        <span style={{marginLeft:"auto",fontSize:12,color:"var(--tx3)",fontWeight:400}}>{open?d.collapse:d.expand} {open?"▲":"▼"}</span>
      </div>
      {open&&<div className="rg">
        {[...employees].sort((a,b)=>{
          const yrsA=a.join_date?(new Date()-new Date(a.join_date)):0;
          const yrsB=b.join_date?(new Date()-new Date(b.join_date)):0;
          return yrsB-yrsA;
        }).map(emp=>{
          const alloc=getAllocatedVac(emp,policyVacTiers);
          const usedV=yearLeaves.filter(l=>l.emp_name===emp.name&&(l.type==="연차"||l.type==="Annual Leave")&&l.status==="approved").reduce((s,l)=>s+parseFloat(l.days||0),0);
          const usedS=yearLeaves.filter(l=>l.emp_name===emp.name&&(l.type==="병가"||l.type==="Sick Leave")&&l.status==="approved").reduce((s,l)=>s+parseFloat(l.days||0),0);
          const remV=Math.max(0,alloc-usedV); const remS=Math.max(0,policySickDays-usedS);
          const vp=Math.min(100,(usedV/alloc)*100); const sp=Math.min(100,(usedS/policySickDays)*100);
          const yrsTotal=emp.join_date?((new Date()-new Date(emp.join_date))/(1000*60*60*24*365.25)):null;
          const yrsInt=yrsTotal?Math.floor(yrsTotal):null;
          const mosInt=yrsTotal?Math.floor((yrsTotal-yrsInt)*12):null;
          const yrsLabel=yrsTotal?(lang==="kr"?`${yrsInt}년 ${mosInt}개월`:lang==="es"?`${yrsInt} a ${mosInt} m`:`${yrsInt}yr ${mosInt}mo`):null;
          return(
            <div key={emp.id} className="rc">
              <div className="rn"><span className="edot"/><span style={{flex:1}}>{emp.name}</span>{yrsLabel&&<span style={{fontSize:9,color:"var(--tx3)"}}>{yrsLabel}</span>}</div>
              <div className="rrow">
                <span style={{color:"var(--tx2)",fontSize:11}}><PlaneTakeoff size={12}/> {d.remainVac}</span>
                <span style={{fontFamily:"'DM Mono',monospace",fontWeight:700,fontSize:12,color:remV===0?"var(--re)":remV<=2?"var(--ye)":"var(--gr)"}}>{remV}/{alloc}{t.dayUnit}</span>
              </div>
              <div className="rbar"><div className="rbf" style={{width:`${vp}%`,background:vp>=100?"var(--re)":vp>=70?"var(--ye)":"var(--gr)"}}/></div>
              <div className="rrow">
                <span style={{color:"var(--tx2)",fontSize:11}}><Pill size={12}/> {d.remainSick}</span>
                <span style={{fontFamily:"'DM Mono',monospace",fontWeight:700,fontSize:12,color:remS===0?"var(--re)":remS<=1?"var(--ye)":"var(--ac)"}}>{remS}/{policySickDays}{t.dayUnit}</span>
              </div>
              <div className="rbar"><div className="rbf" style={{width:`${sp}%`,background:sp>=100?"var(--re)":sp>=70?"var(--ye)":"var(--ac)"}}/></div>
            </div>
          );
        })}
      </div>}
    </div>
  );
}

function HolidayAlert({date,label}){
  if(!date) return null;
  const h=getHolidays(parseInt(date.slice(0,4)))[date];
  if(!h) return null;
  return <div className="hb"><Flag size={11} style={{marginRight:4}}/>{label}: {h}</div>;
}

function DashBottomCards({employees,yearLeaves,recent,lt,t,lang,policySickDays,policyVacTiers}){
  const d=t.dash;
  const [openRec,setOpenRec]=useState(true);
  return(
    <div className="dash-bottom" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
      <RemainCard employees={employees} yearLeaves={yearLeaves} t={t} lang={lang} policySickDays={policySickDays} policyVacTiers={policyVacTiers}/>
      <BirthdayHolidayCard employees={employees} lang={lang}/>
    </div>
  );
}
